<template>
  <div>
    <v-row no-gutters justify="center" align="center">
      <v-col>
        <v-autocomplete
          v-model="client"
          :items="clients"
          :loading="isFetchingClients"
          :error="!hasClient"
          label="Client*"
          placeholder="Client"
          persistent-placeholder
          item-value="id"
          item-text="name"
          return-object
          hide-details
          outlined
          no-data-text="Aucun client"
          class="mr-2"
          v-if="hasToChooseClient"
        />
        <v-text-field
          v-model="clientName"
          readonly
          label="Client*"
          hide-details
          outlined
          class="mr-2"
          v-else
        />
      </v-col>
      <v-col>
        <v-autocomplete
          v-model="site"
          :items="sites"
          :loading="isFetchingSites"
          label="Site*"
          placeholder="Site"
          persistent-placeholder
          item-value="id"
          item-text="name"
          return-object
          hide-details
          outlined
          no-data-text="Aucun site"
          class="ml-2"
          v-if="hasClient"
        />
        <v-text-field
          readonly
          label="Site*"
          placeholder="Merci de choisir un client"
          persistent-placeholder
          hide-details
          outlined
          class="ml-2"
          v-else
        />
      </v-col>
    </v-row>
    <v-text-field
      v-model="recurrentIntervention.title"
      label="Titre de l'intervention*"
      placeholder="Titre de l'intervention"
      persistent-placeholder
      hide-details
      outlined
      class="mt-4"
    />
    <v-textarea
      v-model="recurrentIntervention.description"
      label="Description"
      placeholder="Si besoin ajoutez des détails à cette intervention..."
      persistent-placeholder
      hide-details
      outlined
      append-icon=""
      height="200"
      class="mt-4"
    />
    <v-row no-gutters align="center" justify="center" class="mt-4">
      <v-col>
        <v-autocomplete
          v-model="priority"
          :items="priorities"
          :loading="isFetchingPriorities"
          label="Priorité*"
          placeholder="Priorité"
          persistent-placeholder
          item-value="id"
          item-text="name"
          return-object
          hide-details
          outlined
          no-data-text="Aucune priorité"
          class="mr-2"
        />
      </v-col>
      <v-col>
        <v-autocomplete
          v-model="domain"
          :items="domains"
          :loading="isFetchingDomains"
          label="Domaine*"
          placeholder="Domaine"
          persistent-placeholder
          item-value="id"
          item-text="name"
          return-object
          hide-details
          outlined
          no-data-text="Aucun domaine"
          class="ml-2"
        />
      </v-col>
    </v-row>
    <div class="pt-6">
      <h3 class="pl-1">Équipements concernés</h3>
      <n-card class="form-attachment px-3 mt-2" v-for="equipment in equipments" :key="`equipment-${equipment.id}`">
        <div class="pt-1 pb-1">
          <v-row no-gutters align="center">
            <v-icon color="primary" class="ml-1 mr-2">memory</v-icon>
            <v-col style="font-size: 15px;">
              {{ equipment.label }}
            </v-col>
            <v-btn :disabled="!$canWrite" fab icon depressed color="red" text small class="mr-0" @click="$canWrite && (unlinkEquipment(equipment))">
              <v-icon>delete_outline</v-icon>
            </v-btn>
          </v-row>
        </div>
      </n-card>
      <div class="mt-2" v-if="!hasSelectedSite">
        <v-alert dense outlined type="info">Veuillez sélectionner un site pour ajouter des équipements</v-alert>
      </div>
      <div class="mt-2" v-else>
        <v-menu
          v-model="availableEquipmentsMenu"
          min-width="450"
          offset-y
          :close-on-content-click="false"
          bottom
        >
          <template v-slot:activator="{ attrs }">
            <v-btn
              :disabled="!$canWrite"
              text
              small
              class="ml-0"
              v-bind="attrs"
              @click="$canWrite && (openAvailableEquipmentsMenu($event))"
            >
              <v-icon class="mr-1">add</v-icon>Ajouter un équipement
            </v-btn>
          </template>
          <n-card>
            <v-list dense class="py-0 overflow-y-auto" max-height="400px">
              <div class="pa-3">
                <v-text-field
                  v-model="searchAvailableEquipments"
                  outlined
                  solo
                  flat
                  hide-details
                  autofocus
                  prepend-inner-icon="search"
                  placeholder="Chercher un équipement"
                />
              </div>
              <div class="px-4 py-6" v-if="isFetchingEquipments">
                <v-progress-linear
                  color="primary"
                  indeterminate
                  rounded
                  height="6"
                ></v-progress-linear>
              </div>
              <div class="pa-5 font-italic" v-else-if="!availableEquipments.length">
                Aucun équipement trouvé
              </div>
              <template v-else>
                <template v-for="equipment in availableEquipments">
                  <v-list-item :disabled="!$canWrite" :key="equipment.id" @click="$canWrite && (linkEquipment(equipment))">
                    <v-list-item-content>
                      <v-list-item-title>
                        <template v-if="equipment.number">
                          {{ equipment.number }}
                          <span class="px-1">-</span>
                        </template>
                        {{ equipment.label }}
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="equipment.model || equipment.brand">
                        <template v-if="equipment.brand">
                          {{ equipment.brand }}
                        </template>
                        <template v-if="equipment.model && equipment.brand">
                          <span class="px-1">-</span>
                        </template>
                        <template v-if="equipment.model">
                          {{ equipment.model }}
                        </template>
                      </v-list-item-subtitle>
                      <v-list-item-subtitle>
                        <template v-if="equipment.serialNumber">
                          {{ equipment.serialNumber }}
                        </template>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider :key="`divider-${equipment.id}`" />
                </template>
              </template>
            </v-list>
          </n-card>
        </v-menu>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapGetters } from 'vuex';

export default {
  data: () => ({
    hasToChooseClient: false,
    availableEquipmentsMenu: false,
    searchAvailableEquipments: ``,
    previousCanSubmit: null,
    canSubmit: null,
  }),

  computed: {
    ...mapGetters(`ui/recurrentInterventionForm`, [
      `isFetchingClients`,
      `isFetchingSites`,
      `isFetchingPriorities`,
      `isFetchingDomains`,
      `isFetchingEquipments`,
      `clients`,
      `sites`,
      `priorities`,
      `domains`,
    ]),
    ...mapGetters(`ui/recurrentInterventionForm`, {
      storeRecurrentIntervention: `recurrentIntervention`,
      storeEquipments: `equipments`,
    }),
    recurrentIntervention: {
      get() { return this.storeRecurrentIntervention; },
      set(recurrentIntervention) { this.setRecurrentIntervention(recurrentIntervention); },
    },
    clientName() {
      return this.recurrentIntervention?.client?.name || ``;
    },
    client: {
      get() { return this.recurrentIntervention.client; },
      set(client) {
        this.recurrentIntervention = {
          ...this.recurrentIntervention,
          Client_id: client.id,
          client,
        };
      },
    },
    site: {
      get() { return this.recurrentIntervention.site; },
      set(site) {
        this.recurrentIntervention = {
          ...this.recurrentIntervention,
          Site_id: site.id,
          site,
        };
      },
    },
    priority: {
      get() { return this.recurrentIntervention.priority; },
      set(priority) {
        this.recurrentIntervention = {
          ...this.recurrentIntervention,
          Priority_id: priority.id,
          priority,
        };
      },
    },
    domain: {
      get() { return this.recurrentIntervention.domain; },
      set(domain) {
        this.recurrentIntervention = {
          ...this.recurrentIntervention,
          Domain_id: domain.id,
          domain,
        };
      },
    },
    equipments: {
      get() { return this.recurrentIntervention.equipments || []; },
      set(equipments) {
        this.recurrentIntervention = {
          ...this.recurrentIntervention,
          equipments,
        };
      },
    },
    cleanTitle() {
      return _.trim(this.recurrentIntervention.title || ``);
    },
    cleanDescription() {
      return _.trim(this.recurrentIntervention.description || ``);
    },
    hasClient() {
      return Boolean(this.client?.id);
    },
    hasTitle() {
      return Boolean(this.cleanTitle !== ``);
    },
    hasSelectedClient() {
      return Boolean(this.client?.id);
    },
    hasSelectedSite() {
      return Boolean(this.site && this.site.id);
    },
    hasSelectedPriority() {
      return Boolean(this.priority && this.priority.id);
    },
    hasSelectedDomain() {
      return Boolean(this.domain && this.domain.id);
    },
    availableEquipments() {
      const addedEquipmentsIds = _.map(this.equipments, `id`);
      return _.chain(this.storeEquipments)
        .filter(equipment => !_.includes(addedEquipmentsIds, equipment.id))
        .map(equipment => ({
          ...equipment,
          searchString: _.toLower(_.deburr(`${equipment.number} - ${equipment.label} - ${equipment.brand} - ${equipment.model} - ${equipment.serialNumber}`)),
        }))
        .filter(equipment => _.includes(equipment.searchString, _.toLower(_.deburr(this.searchAvailableEquipments))))
        .value();
    },
  },

  methods: {
    ...mapActions(`ui/recurrentInterventionForm`, [
      `setRecurrentIntervention`,
      `fetchClients`,
      `fetchSites`,
      `fetchPriorities`,
      `fetchDomains`,
      `fetchEquipments`,
    ]),
    async initDatas() {
      const promises = [];
      if (!this.hasClient) {
        promises.push(this.fetchClients());
        this.hasToChooseClient = true;
      }
      await Promise.all([
        ...promises,
        this.fetchSites(),
        this.fetchPriorities(),
        this.fetchDomains(),
        this.fetchEquipments(),
      ]);
      if (!this.hasClient && this.clients.length === 1) {
        [this.client] = this.clients;
      }
      // if (this.sites.length === 1) {
      //   [this.site] = this.sites;
      // }
      if (this.priorities.length === 1) {
        [this.priority] = this.priorities;
      }
      if (this.domains.length === 1) {
        [this.domain] = this.domains;
      }
    },
    openAvailableEquipmentsMenu() {
      this.searchAvailableEquipments = ``;
      this.fetchEquipments();
      this.availableEquipmentsMenu = true;
    },
    linkEquipment(equipment) {
      this.equipments = [
        ...this.equipments,
        equipment,
      ];
      this.availableEquipmentsMenu = false;
    },
    unlinkEquipment(equipment) {
      this.equipments = _.without(this.equipments, equipment);
    },
    computeCanSubmit() {
      this.canSubmit = this.hasTitle
                    && this.hasSelectedClient
                    && this.hasSelectedSite
                    && this.hasSelectedPriority
                    && this.hasSelectedDomain;
    },
  },

  watch: {
    sites() {
      if (this.sites.length === 1) {
        [this.site] = this.sites;
      }
    },
    hasTitle() { this.computeCanSubmit(); },
    client(client, previousClient) {
      if (this.hasSelectedClient && client.id !== previousClient?.id) {
        this.fetchSites();
      }
    },
    hasSelectedClient() { this.computeCanSubmit(); },
    hasSelectedSite() { this.computeCanSubmit(); },
    hasSelectedPriority() { this.computeCanSubmit(); },
    hasSelectedDomain() { this.computeCanSubmit(); },
    canSubmit(canSubmit) {
      if (canSubmit !== this.previousCanSubmit) {
        this.$emit(`isValid`, canSubmit);
        this.previousCanSubmit = canSubmit;
      }
    },
  },

  mounted() {
    this.initDatas();
    this.computeCanSubmit();
  },
};
</script>

<style lang="scss" scoped>
</style>
